/* What just changed, from the audit chain — the reference's activity feed.
 *
 * **The chain is the only source that records changes across every module**, so the feed cannot be
 * incomplete by omission the way one read per module merged in the browser would be. It names the
 * action, who did it and when, and never what changed: the listing route does not select the snapshot,
 * so no payload leaves PostgreSQL on this path, and the feed does not pretend otherwise.
 *
 * **A person and a timer look different**, because "who did this" and "a sweep did this" are different
 * answers to the question a reader chasing an unexpected change is asking. A person gets their initials;
 * a `system:` actor gets the cycle glyph and muted ink.
 *
 * **An action the platform has no sentence for is shown as its identifier, never dropped** — see
 * `activitySentence`. A feed that skipped what it could not phrase would make its own completeness
 * unverifiable.
 */

import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { isSystemActor, useRecentActivity } from '@/features/audit';
import { SCREEN_PATHS } from '@/shared/routes';
import { useReveal } from '@/shared/ui/motion';

import {
  UNRECOGNISED_KEY,
  activityKey,
  activitySentence,
  activityWhen,
  actorInitials,
} from '../model/activity';
import { CycleIcon } from './icons';
import { Panel, Skeleton } from './OverviewParts';

/** How many entries the front page shows. The audit screen has the rest, paged. */
const FEED_ROWS = 6;

export function ActivityPanel({ now }: { readonly now: Date }): ReactElement {
  const { t } = useTranslation();
  const activity = useRecentActivity();
  const entries = activity.isSuccess ? activity.data.items.slice(0, FEED_ROWS) : [];

  const list = useReveal<HTMLUListElement>(
    entries.length > 0,
    (root, timeline) => {
      timeline.from(root.querySelectorAll('.lq-ov-feed__item'), {
        y: 6,
        opacity: 0,
        duration: 0.35,
        stagger: 0.05,
      });
    },
    'rail',
  );

  return (
    <Panel
      title={t('home.activity.title')}
      actions={
        <Link className="lq-ov-link" to={SCREEN_PATHS.audit}>
          {t('home.activity.all')}
        </Link>
      }
      busy={activity.isPending}
      className="lq-ov-feed"
    >
      {activity.isPending ? (
        <Skeleton
          label={t('home.activity.loading')}
          lines={['long', 'medium', 'long', 'medium']}
        />
      ) : activity.isError ? (
        <p className="lq-ov-note" title={activity.error.message}>
          {t('home.activity.unreadable')}
        </p>
      ) : entries.length === 0 ? (
        <p className="lq-ov-note">{t('home.activity.none')}</p>
      ) : (
        <ul ref={list} className="lq-ov-feed__list">
          {entries.map((entry) => {
            const when = activityWhen(entry.occurred_at, now);
            const sentence = activitySentence(entry);
            const system = isSystemActor(entry.actor);
            return (
              <li key={activityKey(entry)} className="lq-ov-feed__item">
                <span
                  className={
                    system ? 'lq-ov-feed__who lq-ov-feed__who--system' : 'lq-ov-feed__who'
                  }
                  aria-hidden="true"
                >
                  {system ? (
                    <CycleIcon className="lq-ov-icon" />
                  ) : (
                    actorInitials(entry.actor)
                  )}
                </span>
                <div className="lq-ov-feed__text">
                  <p className="lq-ov-feed__what">
                    {sentence.kind === 'sentence' ? (
                      t(sentence.key)
                    ) : (
                      <span
                        className="lq-mono"
                        title={t('home.activity.unrecognisedTitle')}
                      >
                        {t(UNRECOGNISED_KEY, {
                          action: sentence.action,
                          kind: sentence.aggregateType,
                        })}
                      </span>
                    )}
                  </p>
                  <p
                    className={
                      system
                        ? 'lq-ov-feed__actor lq-ov-feed__actor--system'
                        : 'lq-ov-feed__actor'
                    }
                  >
                    {entry.actor}
                  </p>
                </div>
                <time className="lq-ov-feed__when" dateTime={entry.occurred_at}>
                  {when === null
                    ? '—'
                    : when.kind === 'time'
                      ? when.at.toLocaleTimeString(undefined, {
                          hour: '2-digit',
                          minute: '2-digit',
                          hourCycle: 'h23',
                        })
                      : t('home.activity.daysAgo', { count: when.days })}
                </time>
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}
